import { ESdChangeReason, EWFArrowType, EWFNodeType, IWFDecision, IWFGateway } from "@kuika/kuika-cl-model";
import { ConditionSetType } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import { WorkFlowDecisionProperties } from "./workflow-decision-properties";
import "./workflow-inclusive-gateway-properties.scss";

export interface IWorkflowInclusiveGatewayPropertiesProps {}

export interface IWorkflowInclusiveGatewayPropertiesState {
  defaultPath?: IWFDecision | undefined;
  decisionArrows: IWFDecision[] | undefined;
  isJoin?: boolean; // if node is join, condition and default UI will be hidden
}

class WorkFlowInclusiveGatewayProperties extends PureSdComponent<
  IWorkflowInclusiveGatewayPropertiesProps & IWithSDProps & IWithContextProp,
  IWorkflowInclusiveGatewayPropertiesState
> {
  constructor(props: IWorkflowInclusiveGatewayPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      decisionArrows: undefined,
      defaultPath: undefined,
      isJoin: false
    };
  }

  componentDidMount() {
    this.prepareDecisionArrows();
  }

  componentDidUpdate(prevProps: IWorkflowInclusiveGatewayPropertiesProps & IWithSDProps & IWithContextProp) {
    if (
      prevProps?.sd.selectedWfNode?.id !== this.props.sd.selectedWfNode?.id ||
      prevProps.sd.selectedWf?.id !== this.props.sd.selectedWf?.id
    ) {
      this.prepareDecisionArrows();
    }
  }

  prepareDecisionArrows = () => {
    const wf = this.props.sd.selectedWf;
    const gwId = this.props.sd.selectedWfNode?.id;

    if (!wf || !gwId) {
      this.setState({ decisionArrows: [], defaultPath: undefined, isJoin: false });
      return;
    }

    const outgoingAll = this.props.sd.findAllNodesRecursively(
      wf,
      EWFNodeType.Arrow,
      (node) => (node as IWFDecision).fromNodeId === gwId
    ) as IWFDecision[];

    const incomingAll = this.props.sd.findAllNodesRecursively(
      wf,
      EWFNodeType.Arrow,
      (node) => (node as IWFDecision).toNodeId === gwId
    );

    const isCanonicalJoin =
      incomingAll.length > 1 && outgoingAll.length === 1 && (outgoingAll[0] as any).arrowType !== EWFArrowType.Decision;

    const isJoin = isCanonicalJoin;

    const decisionArrows: IWFDecision[] = [...outgoingAll].sort((a, b) => {
      const ia = a.internalIndex ?? 0;
      const ib = b.internalIndex ?? 0;
      if (ia !== ib) return ia - ib;
      // eşitse id ile deterministik sırala
      return (a.id || "").localeCompare(b.id || "");
    });

    decisionArrows.forEach((d) => {
      if ((d as any).arrowType !== EWFArrowType.Decision) return;
      if (d.isDefault) return;
      if (!d.condition) {
        d.condition = {
          conditionSet: { conditions: [], type: ConditionSetType.and },
          id: Guid.create().toString(),
          isOrGroup: false
        } as any;
      }
    });

    const gw = this.props.sd.selectedWfNode as (IWFGateway & { userSelectedDefault?: boolean }) | undefined;

    const diagramDefaultId = (gw as any)?.default;
    const userSelectedDefault = gw?.userSelectedDefault === true;

    if (diagramDefaultId) {
      decisionArrows.forEach((o) => {
        const isDef = o.id === diagramDefaultId;
        o.isDefault = isDef;

        const dec = this.props.sd.findNodeRecursively(o.id, EWFNodeType.Arrow, wf) as IWFDecision | undefined;
        if (dec) {
          dec.isDefault = isDef;
        }
      });
    } else if (userSelectedDefault) {
      const modelDefault = decisionArrows.find((d) => d.isDefault);
      if (modelDefault && gw?.id) {
        this.props.sd.updateDiagramElement(gw.id, [
          { name: "default", value: modelDefault.id, isValueIdToGetElement: true }
        ]);
      } else {
        decisionArrows.forEach((d) => (d.isDefault = false));
        if (gw?.id) {
          this.props.sd.updateDiagramElement(gw.id, [{ name: "default", value: null }]);
        }
      }
    } else if (decisionArrows.some((d) => d.isDefault)) {
      decisionArrows.forEach((o) => {
        o.isDefault = false;
        const dec = this.props.sd.findNodeRecursively(o.id, EWFNodeType.Arrow, wf) as IWFDecision | undefined;
        if (dec) dec.isDefault = false;
      });
      if (gw?.id) {
        this.props.sd.updateDiagramElement(gw.id, [{ name: "default", value: null }]);
      }
    }

    const selectedDefault =
      decisionArrows.find((d) => d.isDefault) ||
      ((gw as any)?.default && decisionArrows.find((d) => d.id === (gw as any).default));

    this.setState({
      decisionArrows,
      defaultPath: selectedDefault,
      isJoin
    });
  };

  handleDefaultPathChange = (value?: string | null) => {
    const wf = this.props.sd.selectedWf;
    const list = _.cloneDeep(this.state.decisionArrows) ?? [];

    list.forEach((d) => (d.isDefault = false));
    let selected: IWFDecision | undefined;

    if (value) {
      const idx = list.findIndex((d) => d.id === value);
      if (idx >= 0) {
        list[idx].isDefault = true;
        if ((list[idx] as any).arrowType === EWFArrowType.Decision) {
          delete (list[idx] as any).condition;
        }
        selected = list[idx];
      }
    }

    if (wf) {
      let defaultArrowId: string | undefined;

      list.forEach((o) => {
        const dec = this.props.sd.findNodeRecursively(o.id, EWFNodeType.Arrow, wf) as IWFDecision | undefined;
        if (dec) {
          dec.isDefault = !!o.isDefault;
          if (o.isDefault) {
            if ((dec as any).arrowType === EWFArrowType.Decision) {
              delete (dec as any).condition;
            }
            defaultArrowId = o.id;
          }
        }
      });

      const gw = this.props.sd.selectedWfNode as (IWFGateway & { userSelectedDefault?: boolean }) | undefined;
      if (gw?.id) {
        this.props.sd.updateDiagramElement(gw.id, [
          {
            name: "default",
            value: defaultArrowId ?? null,
            isValueIdToGetElement: !!defaultArrowId
          }
        ]);

        gw.userSelectedDefault = !!defaultArrowId;
      }

      this.props.sd.saveWorkFlow(wf, ESdChangeReason.SaveWorkflow);
    }

    this.setState({ decisionArrows: list, defaultPath: selected });
  };

  renderOptionalDefaultSelector = (decisionArrows: IWFDecision[]) => {
    const { defaultPath } = this.state;

    return (
      <div className="kuika_blue_panel kuika-wf-inclusive-gateway__panel">
        <KuikaText className="kuika-wf-inclusive-gateway__default-caption">Optional default path</KuikaText>
        <div className="kuika-wf-inclusive-gateway__default-field">
          <KuikaSelectbox
            allowClear
            placeholder="No default"
            value={defaultPath?.id}
            onChange={(val: string | null) => this.handleDefaultPathChange(val)}
            options={decisionArrows.map((d: IWFDecision): KuikaSelectboxOption => ({
              key: d.id,
              value: d.id,
              label: d.name
            }))}
          />
        </div>
      </div>
    );
  };

  render() {
    const { decisionArrows, isJoin } = this.state;

    if (isJoin) return <></>;

    if (!decisionArrows) return <></>;

    const editableDecisions = decisionArrows.filter(
      (d) => !d.isDefault && (d as any).arrowType === EWFArrowType.Decision
    );

    return (
      <div>
        {editableDecisions.map((arrow) => (
          <div key={arrow.id} className="kuika_blue_panel kuika-wf-inclusive-gateway__panel">
            <WorkFlowDecisionProperties decisionId={arrow.id} />
          </div>
        ))}

        {decisionArrows.length > 0 && this.renderOptionalDefaultSelector(decisionArrows)}
      </div>
    );
  }

  checkNeedRender = (): boolean => true;
}

const workflowInclusiveGatewayProperties = withContext<IWorkflowInclusiveGatewayPropertiesProps>(
  withSD<IWorkflowInclusiveGatewayPropertiesProps>(WorkFlowInclusiveGatewayProperties)
);

export { workflowInclusiveGatewayProperties as WorkflowInclusiveGatewayProperties };
